export type VerseEnv = 'dev' | 'staging' | 'prod';
export type VerseFlavor = 'web2' | 'web3';

export interface VerseEntry {
  /** Human-readable alias used in UI dropdowns and `?verse=` query param. */
  alias: string;
  /** Actual V8 verseId — '<address>-<timestamp>' format, used by SDK and game-token endpoint. */
  verseId: string;
  env: VerseEnv;
  flavor: VerseFlavor;
  /** Override the SDK default (https://gs-gateway.verse8.io = prod gateway). Leave undefined to use the active gateway toggle. */
  remoteUrl?: string;
  /** preview verses accept unauthenticated connections; prod/staging/dev require a game token. */
  requiresAuth: boolean;
  /** UI display name. */
  label: string;
}

// Verse 매핑 — `.env.<target>` 파일들과 1:1 일치해야 함.
// petpop 계정 (0x2c88f2f06aa707e8a7b0241267a2d63acae515dc) 하위 6개.
export const VERSES: readonly VerseEntry[] = [
  {
    alias: 'dev-w2',
    verseId: '0x2c88f2f06aa707e8a7b0241267a2d63acae515dc-1778600000001',
    env: 'dev',
    flavor: 'web2',
    requiresAuth: true,
    label: 'Dev · Web2',
  },
  {
    alias: 'dev-w3',
    verseId: '0x2c88f2f06aa707e8a7b0241267a2d63acae515dc-1778600000002',
    env: 'dev',
    flavor: 'web3',
    requiresAuth: true,
    label: 'Dev · Web3',
  },
  {
    alias: 'staging-w2',
    verseId: '0x2c88f2f06aa707e8a7b0241267a2d63acae515dc-1778600000003',
    env: 'staging',
    flavor: 'web2',
    requiresAuth: true,
    label: 'Staging · Web2',
  },
  {
    alias: 'staging-w3',
    verseId: '0x2c88f2f06aa707e8a7b0241267a2d63acae515dc-1776312881367',
    env: 'staging',
    flavor: 'web3',
    requiresAuth: true,
    label: 'Staging · Web3',
  },
  {
    alias: 'prod-w2',
    verseId: '0x2c88f2f06aa707e8a7b0241267a2d63acae515dc-3000000000005',
    env: 'prod',
    flavor: 'web2',
    requiresAuth: true,
    label: 'Prod · Web2',
  },
  {
    alias: 'prod-w3',
    verseId: '0x2c88f2f06aa707e8a7b0241267a2d63acae515dc-3000000000006',
    env: 'prod',
    flavor: 'web3',
    requiresAuth: true,
    label: 'Prod · Web3',
  },
] as const;

/** Look up a verse by alias or verseId; returns undefined if not registered. */
export function findVerse(aliasOrId: string): VerseEntry | undefined {
  return VERSES.find((v) => v.alias === aliasOrId || v.verseId === aliasOrId);
}

const PREVIEW_SUFFIX = '-preview';

/**
 * Like `findVerse` but always returns an entry: if `aliasOrId` is not in the
 * registry it synthesizes one from the v8-client verseId convention so manual
 * input (`VerseManualInput`) can connect to arbitrary verses.
 *
 * Heuristic: `<id>-preview` → unauthenticated preview env; anything else is
 * treated as an authenticated prod verseId. v8-client expects a fully-qualified
 * verseId (see v8-client/types.ts), so the `-preview` suffix is preserved.
 */
export function resolveVerseEntry(aliasOrId: string): VerseEntry {
  const registered = findVerse(aliasOrId);
  if (registered) return registered;
  const isPreview = aliasOrId.endsWith(PREVIEW_SUFFIX);
  return {
    alias: aliasOrId,
    verseId: aliasOrId,
    env: 'dev',
    flavor: 'web3',
    requiresAuth: !isPreview,
    label: `Custom: ${aliasOrId}`,
  };
}

/**
 * Return both web2 + web3 verses for a given env. Used by master-data seeding
 * to push to both flavors at once ("dev 시딩 = dev-w2 + dev-w3 동시").
 */
export function getVersesByEnv(env: VerseEnv): readonly VerseEntry[] {
  return VERSES.filter((v) => v.env === env);
}

/**
 * Find the pair verse for a given verse (web2 ↔ web3 within same env).
 * Returns undefined if no pair exists (shouldn't happen with full 6-verse set).
 */
export function findPairVerse(verse: VerseEntry): VerseEntry | undefined {
  return VERSES.find(
    (v) => v.env === verse.env && v.flavor !== verse.flavor && v.alias !== verse.alias
  );
}

/** Default verse used on first load when ?verse= and localStorage are both empty. */
export const DEFAULT_VERSE_ALIAS: string = 'dev-w3';

// ---------------------------------------------------------------------------
// Gateway (V8 GameServer 접속 endpoint) — admin 에서 prod/staging/dev 토글용.
// 세 gateway 에 동일 verseID 가 등록되어 있어 verse 목록은 공유, URL 만 교체한다.
// ---------------------------------------------------------------------------
export type GatewayId = 'prod' | 'staging' | 'dev';

export interface GatewayEntry {
  id: GatewayId;
  url: string;
  /** segmented 버튼 라벨. */
  label: string;
}

export const GATEWAYS: readonly GatewayEntry[] = [
  { id: 'prod', url: 'https://gs-gateway.verse8.io', label: 'Prod' },
  { id: 'staging', url: 'https://gs-gateway.staging.verse8.io', label: 'Staging' },
  { id: 'dev', url: 'https://gs-gateway.develop.verse8.io', label: 'Dev' },
] as const;

/** 첫 로드 기본 gateway — prod(verse8.io). */
export const DEFAULT_GATEWAY_ID: GatewayId = 'prod';

/** id 로 gateway entry 조회. 미등록이면 undefined. */
export function findGateway(id: string): GatewayEntry | undefined {
  return GATEWAYS.find((g) => g.id === id);
}

/** id 의 gateway URL. 미등록이면 default(prod) URL 로 fallback. */
export function gatewayUrlOf(id: GatewayId): string {
  return (findGateway(id) ?? findGateway(DEFAULT_GATEWAY_ID))!.url;
}

/**
 * Resolve the active gateway id from the available sources, preferring the
 * query-param value, then the persisted value, then the default. Unknown values
 * are ignored so stale/garbage input can never poison the selection.
 */
export function resolveGatewayId(queryValue: string | null, storedValue: string | null): GatewayId {
  if (queryValue && findGateway(queryValue)) return queryValue as GatewayId;
  if (storedValue && findGateway(storedValue)) return storedValue as GatewayId;
  return DEFAULT_GATEWAY_ID;
}

/** env → gateway 매핑. VerseEnv 와 GatewayId 는 같은 문자열 집합이지만 타입 안전을 위해 명시화. */
export function gatewayIdForVerseEnv(env: VerseEnv): GatewayId {
  return env;
}

/**
 * gateway env 별 meme-api(VX Shop) origin. prod 는 접두어 없음.
 * gateway URL 의 `develop` 라벨과 일치(dev gateway = gs-gateway.develop.verse8.io).
 */
export function memeApiBaseUrlOf(gatewayId: GatewayId): string {
  switch (gatewayId) {
    case 'staging':
      return 'https://staging.v8-meme-api.verse8.io';
    case 'dev':
      return 'https://develop.v8-meme-api.verse8.io';
    default:
      return 'https://v8-meme-api.verse8.io';
  }
}

/**
 * 메일 지급 워커 base URL — 활성 verse 의 (env, flavor) 에서 파생.
 * dev 는 워커를 배포하지 않으므로 null. staging/prod 만 배포된다.
 * 호스트 스킴은 9c-infra charts/ragnarok-breaker 의 mail-send-worker HTTPRoute
 * override 호스트네임과 1:1 일치해야 한다 (rb-mail-send-<env>-<flavor>.9c.gg).
 */
export function mailSendWorkerUrlOf(env: VerseEnv, flavor: VerseFlavor): string | null {
  if (env === 'dev') return null;
  return `https://rb-mail-send-${env}-${flavor}.9c.gg`;
}
